import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Search,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../components/ui/utils';
import {
  downloadCsv,
  downloadXlsx,
  exportFilename,
} from '../hr-dashboard-utils';
import type { HrFilters } from '../../../types/hr-dashboard.types';
import { HrExportButtons } from './ExportButtons';

export interface HrColumn<T> {
  key: string;
  header: string;
  value: (row: T) => string | number | null | undefined;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  className?: string;
}

export interface HrDataTableProps<T> {
  rows: T[];
  columns: HrColumn<T>[];
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  searchPlaceholder?: string;
  emptyLabel?: string;
  viewName: string;
  filters: HrFilters;
  asOf: string;
  canExport: boolean;
  onBeforeExport: (format: 'csv' | 'xlsx') => Promise<boolean>;
  rowKey?: (row: T, index: number) => string | number;
  maxHeight?: string;
}

export function HrDataTable<T>({
  rows,
  columns,
  initialSort,
  searchPlaceholder = 'Search…',
  emptyLabel = 'No records found',
  viewName,
  filters,
  asOf,
  canExport,
  onBeforeExport,
  rowKey,
  maxHeight = '28rem',
}: HrDataTableProps<T>) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    initialSort || null
  );
  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);

  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = needle
      ? rows.filter((row) =>
          columns.some((column) => {
            const value = column.value(row);
            return String(value ?? '').toLowerCase().includes(needle);
          })
        )
      : rows;

    if (!sort) return filtered;

    const column = columns.find((candidate) => candidate.key === sort.key);
    if (!column) return filtered;

    return [...filtered].sort((left, right) => {
      const a = column.value(left);
      const b = column.value(right);
      if (a === b) return 0;
      if (a === null || a === undefined) return 1;
      if (b === null || b === undefined) return -1;
      const result =
        typeof a === 'number' && typeof b === 'number'
          ? a - b
          : String(a).localeCompare(String(b), undefined, { numeric: true });
      return sort.direction === 'asc' ? result : -result;
    });
  }, [rows, columns, query, sort]);

  const exportRows = () =>
    visibleRows.map((row) => columns.map((column) => column.value(row)));

  const handleExport = async (format: 'csv' | 'xlsx') => {
    if (!canExport) return;
    setExporting(format);
    try {
      const allowed = await onBeforeExport(format);
      if (!allowed) return;

      const headers = columns.map((column) => column.header);
      const data = exportRows();
      const filename = exportFilename(viewName, filters, asOf, format === 'csv' ? 'csv' : 'xlsx');

      if (format === 'csv') downloadCsv(filename, headers, data);
      else downloadXlsx(filename, viewName, headers, data);
    } finally {
      setExporting(null);
    }
  };

  const toggleSort = (column: HrColumn<T>) => {
    if (column.sortable === false) return;
    setSort((current) => {
      if (!current || current.key !== column.key) {
        return { key: column.key, direction: 'asc' };
      }
      return { key: column.key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sortIcon = (column: HrColumn<T>) => {
    if (column.sortable === false) return null;
    if (sort?.key !== column.key) return <ArrowUpDown className="h-3.5 w-3.5 opacity-60" />;
    return sort.direction === 'asc' ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 pl-9"
            aria-label={`Search ${viewName}`}
          />
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <span className="text-xs tabular-nums text-muted-foreground">
            {visibleRows.length} {visibleRows.length === 1 ? 'row' : 'rows'}
          </span>
          {canExport ? (
            <HrExportButtons onExport={handleExport} exporting={exporting} />
          ) : null}
        </div>
      </div>

      {/* Mobile: card stack. Desktop: table. Never a horizontally scrolling table on mobile. */}
      <div className="space-y-2 md:hidden">
        {visibleRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">{emptyLabel}</p>
        ) : (
          visibleRows.map((row, index) => (
            <div
              key={rowKey ? rowKey(row, index) : index}
              className="rounded-lg border border-border p-3"
            >
              <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                {columns.map((column) => (
                  <div key={column.key} className="min-w-0">
                    <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                      {column.header}
                    </dt>
                    <dd className="mt-0.5 truncate text-sm text-foreground">
                      {column.render ? column.render(row) : column.value(row) ?? '—'}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))
        )}
      </div>

      <div className="hidden overflow-auto rounded-md border border-border md:block" style={{ maxHeight }}>
        <Table>
          <TableHeader>
            <TableRow className="bg-muted hover:bg-muted">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={cn(
                    'h-10 px-3 text-xs font-semibold',
                    column.align === 'right' && 'text-right',
                    column.align === 'center' && 'text-center',
                    column.className
                  )}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(column)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      column.sortable === false ? 'cursor-default' : 'hover:text-link',
                      column.align === 'right' && 'ml-auto',
                      column.align === 'center' && 'mx-auto'
                    )}
                    aria-label={`Sort by ${column.header}`}
                  >
                    {column.header}
                    {sortIcon(column)}
                  </button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-8 text-center text-sm text-muted-foreground"
                >
                  {emptyLabel}
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row, index) => (
                <TableRow key={rowKey ? rowKey(row, index) : index}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.key}
                      className={cn(
                        'px-3',
                        column.align === 'right' && 'text-right',
                        column.align === 'center' && 'text-center'
                      )}
                    >
                      {column.render ? column.render(row) : column.value(row) ?? '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
